import type { EnrollmentLandingPageInfo } from '../models/api-data-contracts';
import type { EnrollmentPeriod } from '../models/enrollment-period';

export type EnrollmentWindowStatus = 'active' | 'future' | 'expired' | 'unavailable';

/** Landing payload plus optional N-period list (not present on the generated contract yet). */
export type EnrollmentLandingPageInfoWithPeriods = EnrollmentLandingPageInfo & {
  enrollmentPeriods?: EnrollmentPeriod[] | null;
};

interface EvaluatedWindow {
  status: EnrollmentWindowStatus;
  startDate: string | null;
  endDate: string | null;
  effectiveDate: string | null;
}

/**
 * When `enrollmentPeriods` is present, stamps the first Active else Future window
 * (openDate ascending) onto landing start/end/effective/status.
 * Missing or empty periods leave landing dates unchanged (flag-off / current API).
 */
export function applyResolvedEnrollmentPeriods<T extends EnrollmentLandingPageInfoWithPeriods>(
  info: T,
  now: Date = new Date()
): T {
  if (info.alwaysOpenEnrollment === true) {
    return info;
  }

  const periods = info.enrollmentPeriods;
  if (!periods?.length) {
    return info;
  }

  const sorted = [...periods].sort(compareOpenDateAscending);

  for (const period of sorted) {
    const evaluated = evaluatePeriod(period, now);
    if (evaluated.status === 'active' || evaluated.status === 'future') {
      return applyWindow(info, evaluated);
    }
  }

  const hadOpenDate = sorted.some((period) => parseDateOnly(period.openDate));
  return applyWindow(info, {
    status: hadOpenDate ? 'expired' : 'unavailable',
    startDate: null,
    endDate: null,
    effectiveDate: null,
  });
}

export function compareOpenDateAscending(a: EnrollmentPeriod, b: EnrollmentPeriod): number {
  const aTime = parseDateOnly(a.openDate)?.getTime() ?? Number.POSITIVE_INFINITY;
  const bTime = parseDateOnly(b.openDate)?.getTime() ?? Number.POSITIVE_INFINITY;
  return aTime - bTime;
}

export function parseDateOnly(value: string | Date | null | undefined): Date | null {
  if (value == null || value === '') {
    return null;
  }
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }
    return new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }

  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
}

function evaluatePeriod(period: EnrollmentPeriod, now: Date): EvaluatedWindow {
  const open = parseDateOnly(period.openDate);
  if (!open) {
    return { status: 'unavailable', startDate: null, endDate: null, effectiveDate: null };
  }

  const close = parseDateOnly(period.closeDate);
  if (close && endOfDay(close).getTime() < now.getTime()) {
    return { status: 'expired', startDate: null, endDate: null, effectiveDate: null };
  }

  const effective = resolveEffectiveDate(period.effectiveDate, now);
  if (open.getTime() > now.getTime()) {
    return {
      status: 'future',
      startDate: period.openDate,
      endDate: period.closeDate ?? null,
      effectiveDate: effective,
    };
  }

  return {
    status: 'active',
    startDate: period.openDate,
    endDate: period.closeDate ?? null,
    effectiveDate: effective,
  };
}

function resolveEffectiveDate(effectiveDate: string | null | undefined, now: Date): string {
  const effective = parseDateOnly(effectiveDate);
  const today = parseDateOnly(now);
  if (!effective || !today || effective.getTime() < today.getTime()) {
    return toDateKey(firstDayNextMonth(now));
  }
  return effectiveDate as string;
}

function applyWindow<T extends EnrollmentLandingPageInfoWithPeriods>(info: T, window: EvaluatedWindow): T {
  info.startDate = window.startDate ?? undefined;
  info.endDate = window.endDate ?? undefined;
  info.effectiveDate = window.effectiveDate;
  info.status = window.status;
  return info;
}

function endOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999);
}

function firstDayNextMonth(now: Date): Date {
  return new Date(now.getFullYear(), now.getMonth() + 1, 1);
}

function toDateKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}
